import axios from "axios";

import { MEDIA_PLAYBACK_URLS } from "../ApiEndpoints";
import { getFirebaseIdToken } from "../features/auth/api";

const playbackCache = new Map();
export const PLAYBACK_REFRESH_WINDOW_MS = 5 * 60_000;
export const PLAYBACK_REFRESH_RETRY_MS = 60_000;
const MAX_OBJECT_PATHS_PER_REQUEST = 100;

const durableObjectPath = (value, projectId) => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.replace(/^\/+/, "");
  return normalized.startsWith(`projects/${projectId}/`) ? normalized : null;
};

export const getMediaPlaybackRefreshDelay = (reference, projectId) => {
  const objectPath = durableObjectPath(reference, projectId);
  if (!objectPath) {
    return null;
  }
  const cached = playbackCache.get(objectPath);
  const now = Date.now();
  return Math.max(
    0,
    (cached ? cached.expiresAt - PLAYBACK_REFRESH_WINDOW_MS : now) - now
  );
};

// Players that mount together (a work page's video and its stems) join one
// playback-urls request per project instead of sending one each.
const pendingPlaybackBatches = new Map();

const requestBatchedPlaybackAccess = (objectPath, projectId) => {
  let batch = pendingPlaybackBatches.get(projectId);
  if (!batch || batch.objectPaths.size >= MAX_OBJECT_PATHS_PER_REQUEST) {
    const objectPaths = new Set();
    batch = {
      objectPaths,
      // Sent after the current commit's effects have all added their paths.
      promise: Promise.resolve().then(async () => {
        if (pendingPlaybackBatches.get(projectId) === batch) {
          pendingPlaybackBatches.delete(projectId);
        }
        const firebaseIdToken = await getFirebaseIdToken();
        const response = await axios.post(
          MEDIA_PLAYBACK_URLS,
          { projectId, objectNames: [...objectPaths] },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${firebaseIdToken}`,
            },
          }
        );
        (response.data?.items || []).forEach(cachePlaybackAccess);
      }),
    };
    pendingPlaybackBatches.set(projectId, batch);
  }
  batch.objectPaths.add(objectPath);
  return batch.promise;
};

export const hydrateMediaPlaybackUrl = async (
  reference,
  projectId,
  { forceRefresh = false } = {}
) => {
  const objectPath = durableObjectPath(reference, projectId);
  if (!objectPath) {
    return reference || "";
  }

  if (!forceRefresh) {
    const cached = cachedPlaybackUrl(objectPath);
    if (cached) {
      return cached;
    }
  }

  try {
    await requestBatchedPlaybackAccess(objectPath, projectId);
  } catch (error) {
    console.error("Unable to refresh media playback URL", {
      status: error?.response?.status,
      error:
        error?.response?.data?.error ||
        error?.message ||
        "Unknown media access error",
    });
  }

  return cachedPlaybackUrl(objectPath, { allowNearExpiry: true }) || "";
};

// Download URLs are signed as attachments, so they bypass the playback cache.
export const requestMediaDownloadUrl = async (reference, projectId) => {
  const objectPath = durableObjectPath(reference, projectId);
  if (!objectPath) {
    return null;
  }

  const firebaseIdToken = await getFirebaseIdToken();
  const response = await axios.post(
    MEDIA_PLAYBACK_URLS,
    { projectId, objectNames: [objectPath], download: true },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${firebaseIdToken}`,
      },
    }
  );
  const item = response.data?.items?.[0];
  return item?.playbackUrl ?? item?.playback_url ?? null;
};

export const readAudioClientResponse =(data = {}) => ({
  objectPath: data.objectName ?? data.object_path ?? data.objectPath ?? null,
  playbackUrl: data.playback_url ?? data.playbackUrl ?? data.url ?? null,
  playbackUrlExpiresAt:
    data.playback_url_expires_at ?? data.playbackUrlExpiresAt ?? null,
  durationSeconds:
    data.duration_seconds ??
    data.durationSeconds ??
    data.length ??
    data.audio_length ??
    null,
  emotion: data.emotion ?? null,
});

const cachedPlaybackUrl = (objectPath, { allowNearExpiry = false } = {}) => {
  const cached = playbackCache.get(objectPath);
  if (!cached || cached.expiresAt <= Date.now()) {
    playbackCache.delete(objectPath);
    return null;
  }
  if (
    !allowNearExpiry &&
    cached.expiresAt <= Date.now() + PLAYBACK_REFRESH_WINDOW_MS
  ) {
    return null;
  }
  return cached.url;
};

const cachePlaybackAccess = (item) => {
  const objectPath = item.objectName ?? item.object_path;
  const playbackUrl = item.playbackUrl ?? item.playback_url;
  const expiresAt = Date.parse(
    item.playbackUrlExpiresAt ?? item.playback_url_expires_at
  );
  if (objectPath && playbackUrl && Number.isFinite(expiresAt)) {
    playbackCache.set(objectPath, {
      url: playbackUrl,
      expiresAt,
    });
  }
};

const translationObjectPath = (translation, projectId) =>
  durableObjectPath(
    translation?.translatedAudioPath ??
      translation?.object_path ??
      translation?.objectPath ??
      translation?.translatedAudio,
    projectId
  );

const audioVersionObjectPath = (version, projectId) =>
  durableObjectPath(
    version?.object_path ?? version?.objectPath ?? version?.url,
    projectId
  );

// With languageCode, only the audio a page plays is included: the original and
// that language's current take, not other languages or earlier takes.
const dialogueObjectPaths = (dialogues, projectId, { languageCode } = {}) => {
  const objectPaths = new Set();

  (dialogues || []).forEach((dialogue) => {
    const originalPath = durableObjectPath(
      dialogue?.originalAudioObjectName ?? dialogue?.originalAudio,
      projectId
    );
    if (originalPath) {
      objectPaths.add(originalPath);
    }
    (dialogue?.translations || []).forEach((translation) => {
      if (languageCode && translation?.languageCode !== languageCode) {
        return;
      }
      const path = translationObjectPath(translation, projectId);
      if (path) {
        objectPaths.add(path);
      }
      if (languageCode) {
        return;
      }
      (translation?.translatedAudioList || []).forEach((version) => {
        const versionPath = audioVersionObjectPath(version, projectId);
        if (versionPath) {
          objectPaths.add(versionPath);
        }
      });
    });
  });

  return objectPaths;
};

const replaceSignedPlaybackUrl = (value, objectPath, projectId) =>
  durableObjectPath(value, projectId) || isSignedMediaUrl(value)
    ? objectPath
    : value;

const durableDialoguesForRefresh = (dialogues, projectId) =>
  dialogues.map((dialogue) => {
    const originalObjectName = durableObjectPath(
      dialogue?.originalAudioObjectName ?? dialogue?.originalAudio,
      projectId
    );

    return {
      ...dialogue,
      ...(originalObjectName
        ? {
            originalAudioObjectName: originalObjectName,
            originalAudio: replaceSignedPlaybackUrl(
              dialogue?.originalAudio,
              originalObjectName,
              projectId
            ),
          }
        : {}),
      translations: (dialogue?.translations || []).map((translation) => {
        const objectPath = translationObjectPath(translation, projectId);
        return {
          ...translation,
          ...(objectPath
            ? {
                translatedAudioPath: objectPath,
                translatedAudio: replaceSignedPlaybackUrl(
                  translation?.translatedAudio,
                  objectPath,
                  projectId
                ),
              }
            : {}),
          translatedAudioList: (translation?.translatedAudioList || []).map(
            (version) => {
              const versionPath = audioVersionObjectPath(version, projectId);
              return versionPath
                ? {
                    ...version,
                    objectPath: versionPath,
                    url: replaceSignedPlaybackUrl(
                      version?.url,
                      versionPath,
                      projectId
                    ),
                  }
                : version;
            }
          ),
        };
      }),
    };
  });

export const getDialoguePlaybackObjectKey = (dialogues, projectId, scope) =>
  [...dialogueObjectPaths(dialogues, projectId, scope)].sort().join("\n");

export const getDialoguePlaybackRefreshDelay = (
  dialogues,
  projectId,
  scope
) => {
  const objectPaths = dialogueObjectPaths(dialogues, projectId, scope);
  if (objectPaths.size === 0) {
    return null;
  }

  const refreshTimes = [...objectPaths].map((objectPath) => {
    const cached = playbackCache.get(objectPath);
    return cached
      ? cached.expiresAt - PLAYBACK_REFRESH_WINDOW_MS
      : Date.now();
  });

  return Math.max(0, Math.min(...refreshTimes) - Date.now());
};

export const isSignedMediaUrl = (value) => {
  if (typeof value !== "string" || !value) {
    return false;
  }

  try {
    const url = new URL(value);
    return (
      (url.hostname === "storage.googleapis.com" ||
        url.hostname.endsWith(".storage.googleapis.com")) &&
      (url.searchParams.has("X-Goog-Signature") ||
        url.searchParams.has("X-Goog-Credential"))
    );
  } catch {
    return false;
  }
};

// Audio left out by languageCode keeps its object path, and an empty URL
// until it's signed.
export const hydrateDialoguePlaybackUrls = async (
  dialogues,
  projectId,
  { forceRefresh = false, languageCode } = {}
) => {
  if (!Array.isArray(dialogues) || !projectId) {
    return dialogues || [];
  }

  const durableDialogues = durableDialoguesForRefresh(dialogues, projectId);
  const allObjectPaths = dialogueObjectPaths(durableDialogues, projectId, {
    languageCode,
  });
  const objectPaths = new Set(
    [...allObjectPaths].filter(
      (objectPath) => forceRefresh || !cachedPlaybackUrl(objectPath)
    )
  );

  if (objectPaths.size > 0) {
    try {
      const firebaseIdToken = await getFirebaseIdToken();
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${firebaseIdToken}`,
      };
      const paths = [...objectPaths];
      const responses = await Promise.allSettled(
        Array.from(
          { length: Math.ceil(paths.length / MAX_OBJECT_PATHS_PER_REQUEST) },
          (_, index) =>
            axios.post(
              MEDIA_PLAYBACK_URLS,
              {
                projectId,
                objectNames: paths.slice(
                  index * MAX_OBJECT_PATHS_PER_REQUEST,
                  (index + 1) * MAX_OBJECT_PATHS_PER_REQUEST
                ),
              },
              { headers }
            )
        )
      );
      responses.forEach((result) => {
        if (result.status === "fulfilled") {
          (result.value.data?.items || []).forEach(cachePlaybackAccess);
          return;
        }

        console.error("Unable to refresh generated-audio playback URLs", {
          status: result.reason?.response?.status,
          error:
            result.reason?.response?.data?.error ||
            result.reason?.message ||
            "Unknown media access error",
        });
      });
    } catch (error) {
      console.error("Unable to authenticate media playback URL refresh", {
        error: error?.message || "Unknown Firebase authentication error",
      });
    }
  }

  return durableDialogues.map((dialogue) => {
    const originalObjectName = durableObjectPath(
      dialogue?.originalAudioObjectName ?? dialogue?.originalAudio,
      projectId
    );
    const originalPlaybackUrl = originalObjectName
      ? cachedPlaybackUrl(originalObjectName, { allowNearExpiry: true })
      : null;
    const originalFallback = durableObjectPath(dialogue?.originalAudio, projectId)
      ? ""
      : dialogue?.originalAudio;

    return {
      ...dialogue,
      ...(originalObjectName
        ? {
            originalAudioObjectName: originalObjectName,
            originalAudio: originalPlaybackUrl || originalFallback,
            originalAudioPlaybackUnavailable: !originalPlaybackUrl,
          }
        : {}),
      translations: (dialogue?.translations || []).map((translation) => {
        const objectPath = translationObjectPath(translation, projectId);
        const playbackUrl = objectPath
          ? cachedPlaybackUrl(objectPath, { allowNearExpiry: true })
          : null;
        const translatedAudioFallback = durableObjectPath(
          translation?.translatedAudio,
          projectId
        )
          ? ""
          : translation?.translatedAudio;
        return {
          ...translation,
          ...(objectPath
            ? {
                translatedAudioPath: objectPath,
                translatedAudio: playbackUrl || translatedAudioFallback,
                playbackUnavailable: !playbackUrl,
              }
            : {}),
          translatedAudioList: (translation?.translatedAudioList || []).map(
            (version) => {
              const versionPath = audioVersionObjectPath(version, projectId);
              const versionPlaybackUrl = versionPath
                ? cachedPlaybackUrl(versionPath, { allowNearExpiry: true })
                : null;
              const versionUrlFallback = durableObjectPath(
                version?.url,
                projectId
              )
                ? ""
                : version?.url;
              return versionPath
                ? {
                    ...version,
                    objectPath: versionPath,
                    url: versionPlaybackUrl || versionUrlFallback,
                    playbackUnavailable: !versionPlaybackUrl,
                  }
                : version;
            }
          ),
        };
      }),
    };
  });
};
